import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Send, Copy, Loader2, CheckCircle2, AlertCircle, Mail, ExternalLink, Sparkles } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { sendPaymentLinkEmail } from "@/lib/apiClient";
import { copyTextToClipboard } from "@/lib/clipboard";
import { PaymentProofUploadedPanel } from "@/components/checkout/PaymentReview";

// =====================================================================
// QRPaymentModal — staff-side modal triggered by tapping the QR card.
//
// Renders:
//   * Clinic name + amount (the "professional wrapper" Cameron asked for)
//   * Big QR
//   * "Copy link" + "Send via email" actions to give the patient a
//     /pay/<token> URL.
//   * Live status: pending → proof_submitted → verified
//   * When proof lands: shows the patient's screenshot + a "Verify &
//     use this proof" button that pushes the reference + proof_url
//     back into Step 2.
// =====================================================================

export default function QRPaymentModal({
  open,
  onClose,
  demoMode = false,
  clinicName,
  amountCentavos,
  qrEntry,                       // { id, image_url, label, method } from list_payment_qrs
  appointmentId,
  recipientEmail,                // patient email (for send-link)
  clinicSlug,
  onProofVerified,               // callback({ proof_url, reference_number }) when staff verifies
}) {
  const [link, setLink]          = useState(null);
  const [busy, setBusy]          = useState(false);
  const [error, setError]        = useState(null);
  const [emailTarget, setEmailTarget] = useState(recipientEmail || "");
  const [emailSending, setEmailSending] = useState(false);
  const [verifyRef, setVerifyRef] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [ocrSuggestion, setOcrSuggestion] = useState(null);
  const ocrTriedRef = useRef(false);
  const demoProofUrl = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='420' height='640' viewBox='0 0 420 640'%3E%3Crect width='420' height='640' fill='%23f8fafc'/%3E%3Crect x='34' y='34' width='352' height='572' rx='28' fill='%23ffffff' stroke='%23cbd5e1' stroke-width='3'/%3E%3Ccircle cx='210' cy='128' r='42' fill='%2300a3ff'/%3E%3Cpath d='M188 128l15 15 31-34' fill='none' stroke='white' stroke-width='10' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ctext x='210' y='202' text-anchor='middle' font-family='Arial' font-size='24' font-weight='700' fill='%230f172a'%3EGCash payment sent%3C/text%3E%3Ctext x='210' y='252' text-anchor='middle' font-family='Arial' font-size='42' font-weight='700' fill='%230f172a'%3EP21700.00%3C/text%3E%3Ctext x='64' y='334' font-family='Arial' font-size='18' fill='%23475569'%3EReference No.%3C/text%3E%3Ctext x='64' y='366' font-family='Arial' font-size='24' font-weight='700' fill='%230f172a'%3EGCASH-DEMO-21700%3C/text%3E%3Ctext x='64' y='430' font-family='Arial' font-size='18' fill='%23475569'%3ERecipient%3C/text%3E%3Ctext x='64' y='462' font-family='Arial' font-size='22' font-weight='700' fill='%230f172a'%3ESiteLabs Dental%3C/text%3E%3Ctext x='64' y='526' font-family='Arial' font-size='18' fill='%23475569'%3EStatus%3C/text%3E%3Ctext x='64' y='558' font-family='Arial' font-size='22' font-weight='700' fill='%23059669'%3EProof uploaded by patient%3C/text%3E%3C/svg%3E";

  // Load any existing link for this appointment when opening.
  const loadStatus = useCallback(async () => {
    if (demoMode) return;
    if (!appointmentId) return;
    const { data, error: e } = await supabase.rpc("get_payment_link_status", { p_appointment_id: appointmentId });
    if (e) { setError(e.message ?? "Could not load link status."); return; }
    setLink(data || null);
  }, [appointmentId, demoMode]);

  useEffect(() => { if (open) loadStatus(); }, [open, loadStatus]);

  useEffect(() => {
    if (recipientEmail && !emailTarget.trim()) {
      setEmailTarget(recipientEmail);
    }
  }, [recipientEmail, emailTarget]);

  // Poll every 4s while modal open + status is pending.
  useEffect(() => {
    if (!open) return;
    if (!link || link.status !== "pending") return;
    const t = setInterval(loadStatus, 4000);
    return () => clearInterval(t);
  }, [open, link, loadStatus]);

  // OCR pre-fill: when proof lands, run tesseract.js against the image
  // and try to extract a reference number. Lazy-loaded so the worker
  // bundle (~5MB) only ships when actually needed.
  useEffect(() => {
    if (!open) return;
    if (demoMode) return;
    if (!link?.proof_url) return;
    if (link.status !== "proof_submitted") return;
    if (ocrTriedRef.current) return;
    if (verifyRef) return;
    ocrTriedRef.current = true;
    let cancelled = false;
    (async () => {
      setOcrRunning(true);
      try {
        const Tesseract = await import("tesseract.js");
        const { data } = await Tesseract.recognize(link.proof_url, "eng");
        if (cancelled) return;
        const text = (data?.text || "").replace(/\s+/g, " ");
        // PH GCash/Maya ref-number patterns, ordered most→least specific.
        const patterns = [
          /\b(\d{4}[\s-]\d{3}[\s-]\d{6})\b/,        // GCash: 0000 000 000000
          /\b(\d{4}[\s-]\d{4}[\s-]\d{4})\b/,        // Generic: 0000-0000-0000
          /\bRef(?:\.|erence)?[\s:#]*([A-Z0-9-]{8,})\b/i,
          /\b(\d{12,16})\b/,                         // Long numeric
        ];
        for (const re of patterns) {
          const m = text.match(re);
          if (m) {
            const cand = m[1].replace(/\s/g, "");
            if (!cancelled) setOcrSuggestion(cand);
            break;
          }
        }
      } catch (e) {
        // Non-blocking — if OCR fails, staff types the ref manually.
        // eslint-disable-next-line no-console
        console.warn("[ocr] failed", e);
      }
      if (!cancelled) setOcrRunning(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, link?.proof_url, link?.status]);

  async function handleGenerate() {
    if (demoMode) {
      setError(null);
      setLink({
        id: "tutorial-payment-link",
        token: "tutorial-gcash-payment",
        status: "pending",
      });
      toast.success("Payment link sent.");
      return;
    }
    if (!appointmentId) { setError("Missing appointment id."); return; }
    setBusy(true); setError(null);
    const { data, error: e } = await supabase.rpc("generate_payment_link", {
      p_input: {
        appointment_id:  appointmentId,
        amount_centavos: amountCentavos,
        qr_id:           qrEntry?.id,
      },
    });
    setBusy(false);
    if (e) { setError(e.message ?? "Could not generate link."); return; }
    setLink(data);
  }

  async function handleCancel() {
    if (!link?.id) return;
    if (!window.confirm("Cancel this link? The patient won't be able to pay through it anymore.")) return;
    setBusy(true);
    const { error: e } = await supabase.rpc("cancel_payment_link", { p_link_id: link.id });
    setBusy(false);
    if (e) { setError(e.message ?? "Could not cancel."); return; }
    toast.success("Link cancelled.");
    setLink(null);
  }

  async function handleVerify() {
    if (!link?.id) return;
    if (!verifyRef.trim()) { setError("Type the reference number from the screenshot to verify."); return; }
    if (demoMode) {
      setError(null);
      toast.success("Verified.");
      onProofVerified?.({
        proof_url: link.proof_url || demoProofUrl,
        reference_number: verifyRef.trim(),
      });
      onClose?.();
      return;
    }
    setVerifying(true); setError(null);
    const { data, error: e } = await supabase.rpc("verify_payment_link", {
      p_link_id: link.id, p_reference: verifyRef.trim(),
    });
    setVerifying(false);
    if (e) { setError(e.message ?? "Could not verify."); return; }
    toast.success("Verified.");
    onProofVerified?.({
      proof_url:        data?.proof_url || null,
      reference_number: data?.reference_number || verifyRef.trim(),
    });
    onClose?.();
  }

  async function handleCopy() {
    if (!link?.token) return;
    const url = `${window.location.origin}/pay/${link.token}`;
    try {
      await copyTextToClipboard(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't copy. Long-press the URL to copy manually.");
    }
  }

  async function handleSendEmail() {
    if (!link?.token) return;
    if (demoMode) {
      toast.success("Payment link sent.");
      return;
    }
    if (!emailTarget.trim() || !emailTarget.includes("@")) {
      setError("Enter a valid email."); return;
    }
    if (!clinicName?.trim() || !clinicSlug?.trim()) {
      setError("Clinic details are missing. Reload checkout and try again.");
      return;
    }
    setEmailSending(true); setError(null);
    const res = await sendPaymentLinkEmail({
      to_email:         emailTarget.trim(),
      clinic_name:      clinicName,
      clinic_slug:      clinicSlug,
      token:            link.token,
      amount_centavos:  amountCentavos,
      expires_in_hours: 24,
    });
    setEmailSending(false);
    if (!res?.ok) {
      setError(res?.message || "Couldn't send the email. Copy the link and share it manually.");
      return;
    }
    toast.success("Sent.");
  }

  if (!open) return null;

  const amountStr = (amountCentavos / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const payUrl = link?.token ? `${window.location.origin}/pay/${link.token}` : null;
  const demoProofRow = demoMode && link?.status === "proof_submitted" ? {
    id: link.id,
    appointment_id: appointmentId,
    amount_centavos: amountCentavos,
    currency: "PHP",
    qr_method: qrEntry?.method,
    qr_label: qrEntry?.label,
    status: "proof_submitted",
    proof_url: link.proof_url,
    reference_number: verifyRef || "GCASH-DEMO-21700",
    proof_submitted_at: new Date().toISOString(),
    patients: { full_name: "Juan Dela Cruz", patient_number: "DEMO-0001" },
    appointments: {
      scheduled_start: null,
      chairs: { name: "Chair 1" },
      doctors: { display_name: "Dr. Sample (Demo)" },
    },
  } : null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" data-testid="qr-payment-modal" data-tutorial-id="payment-qr-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[92vh] overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
          <h2 className="text-base font-semibold text-slate-900">Payment</h2>
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-slate-100"
            data-testid="qr-modal-close" aria-label="Close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-3">
          {/* Clinic + amount header — compact */}
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Pay to</p>
            <h3 className="text-base font-bold text-slate-900 mt-0.5">{clinicName}</h3>
            <p className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">₱{amountStr}</p>
          </div>

          {/* QR — compact so the actions stay visible without scrolling */}
          {qrEntry?.image_url ? (
            <div className="bg-white border border-slate-200 rounded-xl p-2 mx-auto" style={{ width: 200 }}>
              <img src={qrEntry.image_url} alt="QR" className="w-full aspect-square object-contain rounded-lg" />
              {qrEntry.label ? <p className="mt-1 text-center text-[11px] text-slate-500">{qrEntry.label}</p> : null}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 text-center">
              No QR uploaded yet. Add one in Settings → QR Codes.
            </div>
          )}

          {/* Status banner — shown whenever there's any prior link */}
          {link ? (() => {
            const tone =
              link.status === "verified"        ? "bg-emerald-50 border border-emerald-100 text-emerald-900"
            : link.status === "proof_submitted" ? "bg-emerald-50 border border-emerald-100 text-emerald-900"
            : link.status === "expired"         ? "bg-slate-100 border border-slate-200 text-slate-700"
            : link.status === "cancelled"       ? "bg-slate-100 border border-slate-200 text-slate-700"
            :                                     "bg-amber-50 border border-amber-100 text-amber-900";
            const message =
              link.status === "verified"        ? "This payment was already verified."
            : link.status === "proof_submitted" ? (demoMode ? "Patient uploaded proof. Verify and continue." : "Patient submitted proof. Verify and continue.")
            : link.status === "expired"         ? "Previous link expired."
            : link.status === "cancelled"       ? "Previous link was cancelled."
            : demoMode                          ? "Payment link sent. Waiting for patient proof."
            :                                     "Link active. Waiting for patient.";
            return (
              <div className={`${tone} rounded-xl px-3 py-2 text-sm flex items-start gap-2`}>
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="flex-1">{message}</span>
              </div>
            );
          })() : null}

          {/* Prior proof preview — visible whenever a proof exists */}
          {demoProofRow ? (
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="qr-modal-proof-review-panel">
              <PaymentProofUploadedPanel row={demoProofRow} compact showCheckoutButton={false} />
            </div>
          ) : link?.proof_url ? (
            <a href={link.proof_url} target="_blank" rel="noopener"
              data-testid="qr-modal-proof-preview"
              data-tutorial-id="payment-proof-preview"
              className="block border border-slate-200 rounded-xl p-2 hover:bg-slate-50">
              <img src={link.proof_url} alt="Patient proof"
                className="w-full max-h-56 object-contain rounded-lg" />
              <p className="mt-1 text-[11px] text-slate-500 text-center inline-flex items-center gap-1 w-full justify-center">
                {link.status === "verified" ? "View receipt" : "Tap to enlarge"} <ExternalLink className="w-3 h-3" />
              </p>
            </a>
          ) : null}

          {/* Verify panel — only when proof needs to be reviewed */}
          {link?.status === "proof_submitted" ? (
            <div className="space-y-2" data-testid="qr-modal-verify-panel">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="verify-ref">
                  Reference / transaction # (from the screenshot)
                </label>
                <input id="verify-ref" type="text"
                  value={verifyRef} onChange={(e) => setVerifyRef(e.target.value)}
                  data-testid="qr-modal-verify-ref"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-teal-500 outline-none" />
                {ocrRunning ? (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />Reading the screenshot…
                  </div>
                ) : ocrSuggestion && ocrSuggestion !== verifyRef ? (
                  <button type="button"
                    onClick={() => setVerifyRef(ocrSuggestion)}
                    data-testid="qr-modal-ocr-suggestion"
                    className="mt-2 w-full inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-3 py-2 text-sm font-semibold text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    <span>Auto-detected reference number:</span>
                    <span className="font-mono font-bold">{ocrSuggestion}</span>
                    <span className="text-amber-700 font-normal">— tap to use</span>
                  </button>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleCancel} disabled={busy}
                  data-testid="qr-modal-reject"
                  className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded">
                  Reject
                </button>
                <button onClick={handleVerify} disabled={verifying || !verifyRef.trim()}
                  data-testid="qr-modal-verify"
                  data-tutorial-id="payment-verify-proof"
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50">
                  {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Verify & use this proof
                </button>
              </div>
            </div>
          ) : null}

          {/* Active link — copy/email/cancel actions while pending */}
          {link?.status === "pending" ? (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">Active link</p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs text-slate-700 break-all" data-testid="qr-modal-pay-url">
                {payUrl}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleCopy}
                  data-testid="qr-modal-copy"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded">
                  <Copy className="w-3.5 h-3.5" />Copy link
                </button>
                <button onClick={handleCancel} disabled={busy}
                  data-testid="qr-modal-cancel"
                  className="ml-auto px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded">
                  Cancel link
                </button>
              </div>
              <div className="space-y-1 pt-1">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="email-target">
                  Send via email
                </label>
                <div className="flex items-center gap-2">
                  <input id="email-target" type="email"
                    value={emailTarget} onChange={(e) => setEmailTarget(e.target.value)}
                    placeholder="patient@example.com"
                    data-testid="qr-modal-email"
                    className="flex-1 px-3 py-1.5 text-sm rounded border border-slate-200 focus:border-teal-500 outline-none" />
                  <button onClick={handleSendEmail} disabled={emailSending || !emailTarget.trim()}
                    data-testid="qr-modal-email-send"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50">
                    {emailSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    Send
                  </button>
                </div>
              </div>
              <div className="text-center text-xs text-slate-400 pt-2 inline-flex items-center justify-center gap-1.5 w-full">
                <Loader2 className="w-3 h-3 animate-spin" />Waiting for patient to upload proof…
              </div>
              {demoMode ? (
                <button
                  type="button"
                  onClick={() => {
                    setVerifyRef("GCASH-DEMO-21700");
                    setLink((prev) => ({
                      ...(prev || {}),
                      id: prev?.id || "tutorial-payment-link",
                      token: prev?.token || "tutorial-gcash-payment",
                      status: "proof_submitted",
                      proof_url: demoProofUrl,
                    }));
                  }}
                  data-testid="qr-modal-demo-proof-uploaded"
                  data-tutorial-id="payment-demo-proof-uploaded"
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-teal-800 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Simulate patient uploaded proof
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Generate / regenerate — visible whenever there's no active pending link
              and no proof currently awaiting review. Always reachable so staff can
              issue a fresh link even after a verified or expired one. */}
          {(!link || link.status === "verified" || link.status === "expired" || link.status === "cancelled") ? (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
                {link ? "Send a new link" : "Send a payment link"}
              </p>
              <p className="text-xs text-slate-500">
                Patient pays from their phone. They'll see the same QR + amount, then upload their receipt.
                You'll see it appear here automatically.
              </p>
              <button onClick={handleGenerate} disabled={busy || !qrEntry?.image_url}
                data-testid="qr-modal-generate"
                data-tutorial-id={demoMode ? "payment-send-link" : undefined}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {demoMode ? "Send to patient" : link ? "Generate new payment link" : "Generate payment link"}
              </button>
            </div>
          ) : null}

          {error ? (
            <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 inline-flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
